import React, { useEffect, useState, useRef } from 'react'
import { supabase } from './supabaseClient'
import { sendEmail } from './emailService'
import { initActivityLogger } from './activityLogger'
import LoginPage, { PageSkeleton } from './components/LoginPage'
import AdminDash   from './components/AdminDash'
import TeacherDash from './components/TeacherDash'
import StudentDash from './components/StudentDash'
import SalesDash   from './components/SalesDash'
import OtpGate, { checkOtpDue } from './components/OtpGate'
import PhoneGate from './components/PhoneGate'

export default function App() {
  const [profile,    setProfile]    = useState(null)
  const [loading,    setLoading]    = useState(true)
  const [isRecovery, setIsRecovery] = useState(false)
  const [otpDue,     setOtpDue]     = useState(false)
  const loadedUserId = useRef(null)

  useEffect(() => {
    // FIX #13: Check URL hash FIRST before any async call.
    // Previously there was a race condition where the hash could be consumed
    // by Supabase's detectSessionInUrl before App.js checked it.
    // Now we read it synchronously on mount, then also listen for the auth event.
    const hash = window.location.hash
    if (hash.includes('type=recovery') || hash.includes('type%3Drecovery')) {
      setIsRecovery(true)
      setLoading(false)
      // Don't return here — still subscribe to auth events so PASSWORD_RECOVERY
      // event confirms the state if the hash check was a false positive.
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && !window.location.hash.includes('type=recovery')) {
        loadProfile(session.user.id)
      } else if (!session && !window.location.hash.includes('type=recovery')) {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true)
        setLoading(false)
        return
      }
      if (event === 'USER_UPDATED') {
        setIsRecovery(false)
        window.history.replaceState(null, '', window.location.pathname)
        // Re-load profile after password reset
        if (session) loadProfile(session.user.id)
        return
      }
      if (event === 'SIGNED_OUT') {
        setProfile(null)
        loadedUserId.current = null
        setLoading(false)
        return
      }
      if (event === 'SIGNED_IN' && session) {
        // Supabase re-fires SIGNED_IN when the browser tab regains focus,
        // not just on an actual new login. Only reload the profile (and
        // remount the dashboard) if this is really a different user.
        if (loadedUserId.current === session.user.id) return
        loadProfile(session.user.id)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Catch the 30-day re-verification window expiring while the tab stays
  // open — loadProfile only checks on login/mount, so without this someone
  // who never reloads would never see the gate. Applies to every role now,
  // not just admin.
  useEffect(() => {
    if (!profile || otpDue) return
    const id = setInterval(async () => {
      if (await checkOtpDue(profile.email)) setOtpDue(true)
    }, 5 * 60 * 1000)
    return () => clearInterval(id)
  }, [profile, otpDue])

  async function loadProfile(userId) {
    setLoading(true)
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (data) {
        setProfile(data)
        loadedUserId.current = userId
        setOtpDue(await checkOtpDue(data.email))
        try { initActivityLogger(data) } catch(_) {}
      } else {
        // Profile missing — create it. This is also a brand-new
        // registration's first login: it has no admin_otp row yet, so
        // checkOtpDue will come back true and the gate shows immediately —
        // no separate "confirm your registration" step needed.
        const { data: { user } } = await supabase.auth.getUser()
        const meta = user?.user_metadata || {}
        const { data: created } = await supabase.from('profiles').insert({
          id:        userId,
          email:     user.email,
          full_name: meta.full_name || user.email?.split('@')[0] || 'User',
          // Always student — user_metadata is set by whoever signs up, so it
          // can't be trusted for a role. Admins promote users in Manage Users.
          role:      'student',
          timezone:  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
        }).select().single()
        if (created) {
          setProfile(created)
          loadedUserId.current = userId
          setOtpDue(await checkOtpDue(created.email))
          try { sendEmail('welcome', created.email, { name: created.full_name }) } catch(_) {}
        }
      }
    } catch(e) {
      console.error('loadProfile error:', e)
    } finally {
      setLoading(false)
    }
  }

  if (loading)                  return <PageSkeleton />
  if (isRecovery || !profile)   return <LoginPage />

  let dash
  switch (profile.role) {
    case 'admin':   dash = <AdminDash   profile={profile} />; break
    case 'teacher': dash = <TeacherDash profile={profile} />; break
    case 'sales':   dash = <SalesDash   profile={profile} />; break
    default:        dash = <StudentDash profile={profile} />
  }
  // DISABLED 2026-09-02, at user's request (same as the OTP gate) — forced
  // to false so PhoneGate never shows for anyone. The gate/component
  // itself is untouched; flip this back to the real check to re-enable.
  // const needsPhone = (profile.role === 'student' || profile.role === 'teacher') && !profile.phone
  const needsPhone = false
  return (
    <>
      {dash}
      {needsPhone
        ? <PhoneGate profile={profile} onSaved={(phone) => setProfile(p => ({ ...p, phone }))} />
        : otpDue && <OtpGate profile={profile} onVerified={() => setOtpDue(false)} />}
    </>
  )
}
